<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import type { Elemental } from '~~/src/types/elemental'
import type { Prompt, PromptMinified } from '~~/src/types/prompt'

interface IProps {
  prompt: Prompt | PromptMinified
  showAddAction?: boolean
  openInNewTab?: boolean
}

interface Emits {
  (e: 'addElemental', prompt: Elemental): void
}

const { prompt } = defineProps<IProps>()

const emits = defineEmits<Emits>()

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smallerOrEqual('sm')
const elementalTypes = useElementalTypes()

const isAdded = ref(false)

useCustomEventListener('form:update-prompt-on-list', ({ elementals }) => {
  isAdded.value = elementals.findIndex(addedPrompt => addedPrompt.id === prompt.id) > -1
})

const elementalType = computed(() => elementalTypes.value.find(t => t.id === prompt.elementalTypeId))

const addPrompt = () => {
  emits('addElemental', unref(prompt) as Elemental)
}

const avatarSize = computed(() => {
  return isMobile.value ? 40 : 60
})

const optimizedPromptAvatar = computed(() => {
  if (!prompt.isPremium)
    return null

  if (!prompt.avatar)
    return '/prompt_avatar.png'

  return optimizeImage(prompt.avatar, {
    width: avatarSize.value * 2, height: avatarSize.value * 2,
  })
})
</script>

<template>
  <div flex items-center gap-3 border-b-1 border-gray-lighter bg-white>
    <slot name="prefix" />

    <NuxtLink :to="`/${elementalType?.name}/${prompt.url || prompt.id}`" :target="openInNewTab ? '_blank' : '_self'" flex flex-grow-1 gap-2 p-3 items-center w-full>
      <img
        v-if="optimizedPromptAvatar" :src="optimizedPromptAvatar"
        :width="avatarSize" :height="avatarSize"
        h-max rounded-2 b-1 border-soft-gray bg-white
        :title="`${prompt.title} avatar`"
        :alt="`${prompt.title} avatar`"
      >

      <div flex="~ col" gap-1>
        <Heading level="5" :max-lines="2">
          {{ prompt.title }}
        </Heading>

        <div flex items-center gap-5>
          <div flex gap-2 items-center>
            <div v-if="prompt.isPrivate">
              <div i-bx:hide text="12px" text-bluish-darker />
            </div>

            <UserChip v-if="prompt.user" :user="prompt.user" variant="text" :open-in-new-tab="openInNewTab" />
          </div>

          <div v-if="typeof prompt.nUses === 'number'" flex gap="2px" items-center>
            <div i-ph-arrow-up-right text-primary />

            <Text variant="caption" text-bluish-darker>
              {{ formatThousands(prompt.nUses) }}
            </Text>
          </div>

          <div v-if="prompt.createdAt">
            <Text variant="small" text-bluish-darker>
              {{ formatTimeAgo(new Date(prompt.createdAt)) }}
            </Text>
          </div>
        </div>
      </div>
    </NuxtLink>

    <div v-if="showAddAction" flex-shrink-0 pr-1>
      <AppButton
        variant="outline" type="button" h-10 pl-4 pr-4
        :enabled="!isAdded"
        @click="!isAdded && addPrompt()"
      >
        <Heading level="5" color="inherit">
          {{ isAdded ? 'Added' : 'Add' }}
        </Heading>
      </AppButton>
    </div>
    <slot name="suffix" />
  </div>
</template>
